/* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions, jsx-a11y/no-noninteractive-element-interactions  */
import {
  forwardRef,
  ForwardRefRenderFunction,
  ReactNode,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';

import * as Portal from '@radix-ui/react-portal';

import { ModalContext, TModalContext } from './context';
import * as S from './styles';

interface IModalProps {
  children?: ReactNode;
  initialOpen?: boolean;
  closeOnOverlayClick?: boolean;
  align?: S.TAlignment;
  /**
   * Coloca o modal acima dos demais. Use quando ele for aberto a partir de outro
   * modal, ja que a ordem de montagem no portal decidiria quem fica na frente.
   */
  elevated?: boolean;
  onClose?: () => void;
}

const Modal: ForwardRefRenderFunction<TModalContext, IModalProps> = (
  {
    children,
    initialOpen = false,
    closeOnOverlayClick = true,
    align = 'center',
    elevated = false,
    onClose,
  },
  ref,
) => {
  const [visible, setVisible] = useState(initialOpen);

  const actions = useMemo(
    () => ({
      visible,
      close: () => {
        setVisible(false);
        onClose?.();
      },
      open: () => setVisible(true),
      toggle: () => setVisible(visible => !visible),
    }),
    [visible, onClose],
  );

  useImperativeHandle(ref, () => actions, [actions]);

  useEffect(() => {
    document.body.style.overflow = visible ? 'hidden' : 'auto';

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [visible]);

  return (
    <Portal.Root>
      <ModalContext.Provider value={actions}>
        <S.Overlay
          visible={visible}
          align={align}
          elevated={elevated}
          onClick={() => {
            if (!closeOnOverlayClick) return;

            setVisible(false);
            onClose?.();
          }}
        >
          <div
            className="modal-dialog"
            onClick={event => event.stopPropagation()}
            role="dialog"
          >
            {children}
          </div>
        </S.Overlay>
      </ModalContext.Provider>
    </Portal.Root>
  );
};

export const ModalConsumer = ModalContext.Consumer;

export default forwardRef(Modal);
export { useModal } from './context';
